import { CloseOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { DeletedFilled } from 'assets/images/svg';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import { useManagePermittedRoles } from 'library/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectSites } from 'reduxStore/sites/selectors';
import { selectTeams } from 'reduxStore/teams/selectors';
import { teamsActions } from 'reduxStore/teams/slice';
import { WarningAlert } from 'zComponents/atoms';
import {
	Avatar,
	Button,
	Col,
	Divider,
	Empty,
	Flex,
	Form,
	Input,
	List,
	Modal,
	Row,
	Select,
	Space,
	Tooltip,
	Typography
} from 'zui/Antd';
import { formatDisplayName } from 'library/helpers/utility';
import { getInitials } from 'library/helpers/getInitials';

const { Title, Text } = Typography;
const { TextArea } = Input;

const AddTeamModal = ({ children, visible: visibleProp, setVisible: setVisibleProp }) => {
	// ✅ Hooks
	const dispatch = useDispatch();
	const canAddTeam = useManagePermittedRoles('addTeam');
	const isGlobalAdmin = useManagePermittedRoles('manageSiteAssignment');
	const permissionDeniedMessage = "You don't have permission to perform this action.";
	const {
		token: {
			fontSizeSM,
			paddingXL,
			paddingLG,
			colorTextDescription,
			fontWeightStrong,
			paddingXS,
			paddingSM,
			colorBorder,
			borderMD = 6,
			paddingXXS,
			colorTextLabel,
			colorText
		}
	} = theme.useToken();
	const [form] = Form.useForm();

	// 🛡️ Multi-tenancy: Get permitted sites
	const permittedSites = useSelector(selectSites);

	// ✅ Redux States
	const teamsData = useSelector(selectTeams);
	const peopleData = useSelector(selectPeople);

	const { addTeamLoading, completeAddTeam } = useSelector((state) => state.Teams);

	// ✅ Local States
	const [selectedPeople, setSelectedPeople] = useState([]);
	const [internalVisible, setInternalVisible] = useState(false);

	// ✅ Handlers
	const visible = visibleProp !== undefined ? visibleProp : internalVisible;
	const setVisible = setVisibleProp !== undefined ? setVisibleProp : setInternalVisible;

	// 🎯 Native Form Initialization for Site Selection
	useEffect(() => {
		if (visible && !isGlobalAdmin) {
			const initialSite = permittedSites?.length > 0 ? permittedSites[0].siteID : null;
			if (initialSite) {
				form.setFieldsValue({ siteID: initialSite });
			}
		}
	}, [visible, permittedSites, form, isGlobalAdmin]);

	// ✅ Effect to handle the completion of adding a team
	useEffect(() => {
		if (completeAddTeam) {
			setTimeout(() => {
				setVisible(false);
				form.resetFields();
				setSelectedPeople([]);
			}, 500); // Small delay to ensure UI updates properly
		}
	}, [completeAddTeam, form, setVisible]);

	// ✅ Handlers
	const handleCancel = () => {
		setVisible(false);
		form.resetFields();
		setSelectedPeople([]);
	};

	// ✅ Submit the form
	const handleSubmit = (values) => {
		dispatch(
			teamsActions.addTeam({
				data: { ...values, personIDs: selectedPeople.map((person) => person.personID) }
			})
		);
	};

	// ✅ Select Person
	const handleSelectPerson = (personID) => {
		const selectedPerson = peopleData?.find((p) => p.personID === personID);
		if (selectedPerson && !selectedPeople.some((p) => p.personID === personID)) {
			setSelectedPeople([...selectedPeople, selectedPerson]);
		}
	};

	// ✅ Remove Person
	const handleRemovePerson = (personID) => {
		setSelectedPeople((prev) => prev.filter((p) => p.personID !== personID));
	};

	// 📍 Watch Selected Site to filter available people options
	const selectedSiteID = Form.useWatch('siteID', form);

	// ✅ People Options (Filtered by selected site so you can't add people from other sites to this team)
	const availablePeopleOptions = useMemo(() => {
		return peopleData
			.filter(
				(person) =>
					person.siteID === selectedSiteID && // 📍 Only show people belonging to the selected site
					!selectedPeople.some((p) => p.personID === person.personID)
			)
			.map((person) => ({
				label: formatDisplayName(person)?.displayName || '-',
				value: person.personID
			}));
	}, [peopleData, selectedPeople, selectedSiteID]);

	const [showAlert, setShowAlert] = useState(false);
	const [showTeamAlert, setShowTeamAlert] = useState(false);
	const nameValue = Form.useWatch('name', form);

	// 📍 Modified Validation: Check duplicate names only within the same site
	useEffect(() => {
		if (nameValue && selectedSiteID) {
			const isDuplicate = teamsData.some(
				(team) => team.siteID === selectedSiteID && team.name.toLowerCase() === nameValue.toLowerCase()
			);
			setShowAlert(isDuplicate);
		} else {
			setShowAlert(false);
		}
	}, [teamsData, nameValue, selectedSiteID]);

	const selectedIDs = useMemo(() => {
		return selectedPeople.map((p) => p.personID).sort((a, b) => a - b);
	}, [selectedPeople]);

	// 📍 Modified Validation: Check exact duplicate teams only within the same site
	useEffect(() => {
		if (selectedIDs.length === 0 || !selectedSiteID) {
			setShowTeamAlert(false);
			return;
		}

		const duplicate = teamsData.some((team) => {
			if (team.siteID !== selectedSiteID) return false;
			if (!Array.isArray(team.people)) return false;

			const teamIDs = [...team.people].sort((a, b) => a - b);
			return teamIDs.length === selectedIDs.length && teamIDs.every((id, idx) => id === selectedIDs[idx]);
		});

		setShowTeamAlert(duplicate);
	}, [teamsData, selectedIDs, selectedSiteID]);

	return (
		<>
			<Tooltip title={!canAddTeam ? permissionDeniedMessage : 'Create Team'}>
				<span>
					{children ? (
						children
					) : (
						<Button type='primary' disabled={!canAddTeam} onClick={() => canAddTeam && setVisible(true)}>
							Create Team
						</Button>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				destroyOnClose
				width={1154}
				closeIcon={<CloseOutlined />}
				centered
				footer={[
					<Button key='cancel' onClick={handleCancel}>
						Cancel
					</Button>,
					<Button
						htmlType='submit'
						type='primary'
						key='submit'
						form='addTeamForm' // 📍 Explicitly linked to form ID
						loading={addTeamLoading}
						disabled={addTeamLoading || showAlert || showTeamAlert}
					>
						Create Team
					</Button>
				]}
				styles={{
					body: { height: 617, display: 'flex', flexDirection: 'column' },
					content: { padding: paddingXL, height: '100%' },
					footer: { marginTop: 0 }
				}}
			>
				<Row style={{ height: '100%' }}>
					{/* Left Panel */}
					<Col span={11} style={{ height: '100%' }}>
						<Flex vertical style={{ marginBottom: paddingXL }}>
							<Title level={4} style={{ margin: 0 }}>
								Create Team
							</Title>
							<Text style={{ fontSize: fontSizeSM, color: colorTextDescription, lineHeight: '20px' }}>
								A team is a group of people that helps to streamline permission management. When a
								person is added to a team, they automatically inherit all permissions assigned to that
								team.
							</Text>
						</Flex>

						<Form
							id='addTeamForm'
							onFinish={handleSubmit}
							form={form}
							layout='vertical'
							requiredMark={false}
						>
							<Row style={{ marginBottom: 0 }} gutter={[12, paddingLG]} align={'middle'}>
								{/* ✅ Group Name & Description */}
								<Col span={24}>
									<Form.Item
										name='name'
										label='Team Name'
										rules={fieldsValidator.Name}
										style={{ fontWeight: fontWeightStrong, marginBottom: 0 }}
									>
										<Input placeholder='e.g. General Employees' />
									</Form.Item>
									{showAlert && (
										<WarningAlert message='A team with this name already exists in the selected site' />
									)}
								</Col>
								{/* 📍 MULTI-TENANT SITE SELECTOR */}
								<Col span={24}>
									<Form.Item
										name='siteID'
										label='Select Site'
										rules={[
											{ required: !isGlobalAdmin, message: 'Please select a site to create this team.' }
										]}
										style={{ fontWeight: fontWeightStrong, marginBottom: 0 }}
										extra={
											!isGlobalAdmin && permittedSites?.length <= 1 && (
												<Text type='secondary' style={{ fontSize: 12, fontWeight: 'normal' }}>
													This team will automatically be created in your assigned site.
												</Text>
											)
										}
									>
										<Select
											placeholder='Select a site'
											allowClear={isGlobalAdmin}
											disabled={!isGlobalAdmin && permittedSites?.length <= 1}
											options={permittedSites?.map((s) => ({
												label: s.name,
												value: s.siteID
											}))}
											onChange={() => {
												// Clear selected people if site changes,
												// because people belong to specific sites.
												setSelectedPeople([]);
											}}
										/>
									</Form.Item>
								</Col>
								<Col span={24}>
									<Form.Item
										name='description'
										label='Team Description'
										style={{ fontWeight: fontWeightStrong, marginBottom: 0 }}
									>
										<TextArea
											style={{ color: colorTextDescription }}
											placeholder='This team is...'
											autoSize={{ minRows: 4, maxRows: 6 }}
										/>
									</Form.Item>
								</Col>
							</Row>
						</Form>
					</Col>

					{/* Divider */}
					<Col span={2} style={{ display: 'flex', justifyContent: 'center', height: '100%' }}>
						<Divider type='vertical' style={{ height: '100%', margin: `0px ${paddingXS}px` }} />
					</Col>

					{/* Right Panel */}
					<Col span={11} style={{ height: '100%' }}>
						{/* ✅ People Connect Section */}
						<Space direction='vertical' size={paddingXS} style={{ width: '100%', marginBottom: paddingLG }}>
							<Text style={{ fontWeight: fontWeightStrong }}>Add People to Team (Optional)</Text>
							<Select
								showSearch
								placeholder={!selectedSiteID ? 'Select a site first' : 'Enter Person Name(s)'}
								style={{ width: '100%', marginBottom: 0 }}
								options={availablePeopleOptions}
								value={null}
								disabled={!selectedSiteID} // 📍 Disable if no site selected
								onSelect={(value) => handleSelectPerson(value)}
								filterOption={(input, option) =>
									option?.label?.toLowerCase().includes(input.toLowerCase())
								}
							/>
						</Space>
						{/* ✅ People List */}
						<Flex gap={paddingXS} vertical>
							<Flex align='center' justify='space-between'>
								<Text strong>Selected People</Text>
								<Button
									type='text'
									onClick={() => setSelectedPeople([])}
									style={{ textDecoration: 'underline' }}
								>
									Clear List
								</Button>
							</Flex>

							<Flex
								style={{
									height: 378,
									overflowY: 'auto',
									padding: `${paddingSM}px 0px`,
									borderRadius: borderMD,
									border: `1px solid ${colorBorder}`,
									width: 'calc(100% - 2px)'
								}}
							>
								{selectedPeople && selectedPeople.length > 0 ? (
									<List
										dataSource={selectedPeople}
										split={false}
										style={{ width: '100%' }}
										renderItem={(person, index) => (
											<List.Item
												key={person.personID}
												style={{
													padding: `${paddingXXS}px 20px`,
													width: '100%',
													borderBottom: 'none',
													marginBottom: index < selectedPeople.length - 1 ? paddingXXS : 0
												}}
												extra={
													<Button
														icon={<DeletedFilled width={16} height={16} />}
														onClick={() => handleRemovePerson(person.personID)}
														type='text'
														style={{ color: colorTextLabel }}
													/>
												}
											>
												<Flex align='center' gap={paddingSM}>
													<Avatar style={{ fontWeight: fontWeightStrong, color: colorText }}>
														{getInitials(person?.displayName) || '-'}
													</Avatar>
													<Text style={{ fontWeight: 400 }}>
														{person?.displayName || '-'}
													</Text>
												</Flex>
											</List.Item>
										)}
									/>
								) : (
									<Flex align='center' justify='center' style={{ height: '100%', width: '100%' }}>
										<Empty
											description={
												!selectedSiteID ? 'Select a site first' : 'Add people to this team'
											}
											image={Empty.PRESENTED_IMAGE_SIMPLE}
										/>
									</Flex>
								)}
							</Flex>
						</Flex>
						{showTeamAlert && (
							<Flex style={{ padding: `${paddingSM}px 0px`, width: '100%' }} justify='center'>
								<WarningAlert message='A Team with these people already exists in this site.' />
							</Flex>
						)}
					</Col>
				</Row>
			</Modal>
		</>
	);
};

export default AddTeamModal;
